import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useEffect, useMemo, useRef, useState } from 'react';

import useTranslations from '@/i18n/useTranslations';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import PasswordEyeOpen from '@/shared-components/icons/components/forms/PasswordEyeOpen';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import ConfirmationDialog from '@/shared-components/popups/components/ConfirmationDialog';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import { formatCurrencyAmount } from '@/utils/currency.utils';

import {
  createEmptyTier,
  getSupportCurrencySymbol,
  normaliseSupportConfig,
  normaliseSupportCurrency,
  SUPPORT_CURRENCY_OPTIONS,
  SUPPORT_PITCH_MAX_LENGTH,
  SUPPORT_TITLE
} from './constants';
import SupportBackgroundCropper from './SupportBackgroundCropper';
import SupportPreview from './SupportPreview';
import SupportTierRow from './SupportTierRow';

import './support.css';

const MAX_TIERS = 3;

/**
 * Internal setup popup shown to profile owners. It allows them to configure
 * the support experience while seeing a live preview.
 */
export default function SupportSetupPopup({ open, onClose, onSave, initialConfig, supportedUser = null }) {
  const t = useTranslations('common.profile.buttons.support.setup');
  const [draft, setDraft] = useState(() => normaliseSupportConfig(initialConfig));
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [errors, setErrors] = useState({});
  const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
  const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);
  const backgroundInputRef = useRef(null);
  // First-time or disabled state should publish support publicly.
  const primaryButtonLabel = initialConfig?.enabled ? t('actions.save_changes') : 'Publish';

  useEffect(() => {
    if (open) {
      setDraft(normaliseSupportConfig(initialConfig));
      setErrors({});
      setIsMobilePreviewOpen(false);
      setShowDisableConfirmation(false);
    }
  }, [open, initialConfig]);

  // Enable drag & drop reordering with a small activation distance so accidental drags are avoided.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const formattedPreview = useMemo(() => {
    const cleanTiers = draft.tiers
      .map(tier => ({
        ...tier,
        amount: tier.amount === '' ? '' : Math.max(1, Number(tier.amount))
      }))
      .filter(tier => (tier.label && tier.label.trim()) || tier.amount);

    const cleanDefaultAmounts = Array.isArray(draft.defaultAmounts)
      ? draft.defaultAmounts.map(slot => {
        if (slot.isCustom) {
          return { ...slot, isCustom: true };
        }

        const numericValue = Number(slot.amount);
        if (slot.amount === '' || Number.isNaN(numericValue)) {
          return { ...slot, amount: '' };
        }

        return { ...slot, amount: Math.max(3, Math.round(numericValue)) };
      })
      : [];

    return {
      ...draft,
      tiers: cleanTiers,
      defaultAmounts: cleanDefaultAmounts
    };
  }, [draft]);
  const currencySymbol = getSupportCurrencySymbol(draft.currency);
  const defaultMinimumAmount = formatCurrencyAmount(3, draft.currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }) || (draft.currency === 'EUR' ? `3 ${currencySymbol}` : `${currencySymbol}3`);
  const pitchValue = typeof draft.pitch === 'string' ? draft.pitch : '';
  const pitchRemaining = Math.max(0, SUPPORT_PITCH_MAX_LENGTH - pitchValue.length);

  function handleCurrencyChange(nextCurrency) {
    setDraft(prev => ({
      ...prev,
      currency: normaliseSupportCurrency(nextCurrency)
    }));
  }

  function handlePitchChange(event) {
    const { value } = event.target;
    setDraft(prev => ({
      ...prev,
      pitch: value.slice(0, SUPPORT_PITCH_MAX_LENGTH)
    }));
  }

  // Persist field edits for a tier while preserving the rest of the array.
  function handleTierChange(updatedTier) {
    setDraft(prev => ({
      ...prev,
      tiers: prev.tiers.map(tier => tier.id === updatedTier.id ? updatedTier : tier)
    }));
  }

  // Remove a tier by id; the UI prevents going below two tiers.
  function handleTierRemove(id) {
    setDraft(prev => ({
      ...prev,
      tiers: prev.tiers.filter(tier => tier.id !== id)
    }));
  }

  // Append an empty tier while respecting the four tier cap.
  function handleAddTier() {
    setDraft(prev => ({
      ...prev,
      tiers: prev.tiers.length >= MAX_TIERS ? prev.tiers : [...prev.tiers, createEmptyTier()]
    }));
  }

  function handleDefaultAmountChange(index, value) {
    setDraft(prev => {
      const nextDefaults = Array.isArray(prev.defaultAmounts) ? prev.defaultAmounts : [];
      return {
        ...prev,
        defaultAmounts: nextDefaults.map((slot, slotIndex) => {
          if (slotIndex !== index || slot.isCustom) {
            return slot;
          }

          if (value === '') {
            return { ...slot, amount: '' };
          }

          const numericValue = Number(value);
          if (Number.isNaN(numericValue)) {
            return slot;
          }

          if (numericValue < 0) {
            return { ...slot, amount: '' };
          }

          return { ...slot, amount: value };
        })
      };
    });
  }

  // Re-order tiers when the user finishes a drag interaction.
  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setDraft(prev => {
      const oldIndex = prev.tiers.findIndex(tier => tier.id === active.id);
      const newIndex = prev.tiers.findIndex(tier => tier.id === over.id);
      return {
        ...prev,
        tiers: arrayMove(prev.tiers, oldIndex, newIndex)
      };
    });
  }

  // Validate the configuration before persisting it to the parent component.
  function handleValidateAndSave() {
    const cleanedTiers = draft.tiers
      .map(tier => ({
        ...tier,
        amount: tier.amount === '' ? '' : Math.max(1, Number(tier.amount))
      }))
      .filter(tier => tier.label.trim() && Number(tier.amount) >= 1);

    const cleanedGoalAmount = draft.goal.amount ? Math.max(1, Number(draft.goal.amount)) : '';
    const cleanedDefaultAmounts = Array.isArray(draft.defaultAmounts)
      ? draft.defaultAmounts.map(slot => {
        if (slot.isCustom) {
          return { ...slot, isCustom: true };
        }

        if (slot.amount === '') {
          return { ...slot, amount: '' };
        }

        const numericValue = Number(slot.amount);
        if (Number.isNaN(numericValue)) {
          return { ...slot, amount: '' };
        }

        return { ...slot, amount: Math.max(3, Math.round(numericValue)) };
      })
      : [];

    const newErrors = {};
    if (cleanedTiers.length < 2) {
      newErrors.tiers = t('errors.add_at_least_two_tiers', { currencySymbol });
    }

    if (draft.goal.amount && Number(draft.goal.amount) < 1) {
      newErrors.goal = t('errors.goal_minimum', { currencySymbol });
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onSave({
        ...draft,
        enabled: true,
        title: SUPPORT_TITLE,
        tiers: cleanedTiers,
        defaultAmounts: cleanedDefaultAmounts,
        allowCustomAmount: true,
        goal: {
          amount: cleanedGoalAmount,
          label: draft.goal.label
        }
      });
      onClose();
    }
  }

  function handleDisable() {
    onSave({ ...draft, enabled: false, title: SUPPORT_TITLE, allowCustomAmount: true });
    onClose();
  }

  // When the user picks a new background image, open the cropper for the 3:4 ratio.
  function handleBackgroundChange(event) {
    const [file] = event.target.files || [];
    if (!file) {
      return;
    }

    setImageToCrop(file);
    setShowCropper(true);
    event.target.value = '';
  }

  // Centralised handler for both accepting and cancelling the crop flow.
  function handleCropComplete(file) {
    setShowCropper(false);
    setImageToCrop(null);

    if (!file) {
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setDraft(prev => {
      if (prev.backgroundImage?.previewUrl) {
        URL.revokeObjectURL(prev.backgroundImage.previewUrl);
      }

      return {
        ...prev,
        backgroundImage: {
          file,
          previewUrl
        }
      };
    });
  }

  useEffect(() => {
    const currentPreview = draft.backgroundImage?.previewUrl;
    return () => {
      if (currentPreview) {
        URL.revokeObjectURL(currentPreview);
      }
    };
  }, [draft.backgroundImage?.previewUrl]);

  // Prevent background listeners from immediately closing the popup during inner interactions.
  function stopEventPropagation(event) {
    event.stopPropagation();
  }

  return (
    <GenericPopup open={open} onBackgroundClick={onClose} className="support-setup-popup remove-scrollbar" opacity>
      <div
        className={`support-setup${isMobilePreviewOpen ? ' support-setup--preview-visible' : ''}`}
        onClick={stopEventPropagation}
        onMouseDown={stopEventPropagation}
        onTouchStart={stopEventPropagation}
      >
        <header className="support-setup__header">
          <div className="support-setup__header-copy">
            <h2 className="generic-popup__header-text">{t('header.title')}</h2>
          </div>
          <button
            type="button"
            className="support-setup__preview-toggle"
            aria-label={t('aria.open_support_preview')}
            onClick={() => setIsMobilePreviewOpen(true)}
          >
            <PasswordEyeOpen />
          </button>
          <XIcon className="support-setup__close" onClick={onClose} />
        </header>

        <div className="support-setup__body">
          <div className="support-setup__form">
            <div className="support-setup__pitch-field">
              <PublicFormInput
                className="support-setup__pitch-input"
                placeholder={t('fields.pitch')}
                type="textarea"
                maxRows={4}
                autoResize
                maxLength={SUPPORT_PITCH_MAX_LENGTH}
                value={draft.pitch}
                onChange={handlePitchChange}
              />
              <span
                className="support-setup__pitch-counter"
                aria-label={t('aria.characters_remaining', { count: pitchRemaining })}
              >
                {pitchRemaining}
              </span>
            </div>

            <div className="support-setup__section">
              <div className="support-setup__section-header">
                <span>{t('sections.background_image')}</span>
                <input
                  ref={backgroundInputRef}
                  className="support-setup__upload-input"
                  type="file"
                  accept="image/*"
                  onChange={handleBackgroundChange}
                />
                <ContinueButton
                  type="button"
                  className="support-setup__upload"
                  onClick={() => backgroundInputRef.current?.click()}
                >
                  {t('actions.upload')}
                </ContinueButton>
              </div>
              {draft.backgroundImage?.previewUrl && (
                <img
                  className="support-setup__background-preview"
                  src={draft.backgroundImage.previewUrl}
                  alt={t('sections.support_background_alt')}
                />
              )}
            </div>

            <div className="support-setup__section">
              <div className="support-setup__section-header">
                <span>{t('sections.suggested_tiers')}</span>
                <ContinueButton
                  type="button"
                  onClick={handleAddTier}
                  className="support-setup__add-tier"
                  turnOff={draft.tiers.length >= MAX_TIERS}
                >
                  {t('actions.add_tier')}
                </ContinueButton>
              </div>

              {errors.tiers && <p className="support-setup__error">{errors.tiers}</p>}

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={draft.tiers.map(tier => tier.id)} strategy={verticalListSortingStrategy}>
                  {draft.tiers.map(tier => (
                    <SupportTierRow
                      key={tier.id}
                      tier={tier}
                      currencySymbol={currencySymbol}
                      onChange={handleTierChange}
                      onRemove={handleTierRemove}
                      disableRemove={draft.tiers.length <= 2}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>

            <div className="support-default-amount">
              <div className="support-default-amount__header">
                <span>{t('default_amounts.title')}</span>
              </div>
              <div className="support-default-amount__grid">
                {Array.isArray(draft.defaultAmounts) && draft.defaultAmounts.map((slot, index) => (
                  <div
                    key={slot.id || index}
                    className={`support-default-amount__item ${slot.isCustom ? 'support-default-amount__item--custom' : ''}`}
                  >
                    {slot.isCustom ? (
                      <div className="support-default-amount__custom">
                        <PublicFormInput
                          className="support-default-amount__currency-select"
                          placeholder={t('default_amounts.currency')}
                          type="select"
                          element="support_currency"
                          value={draft.currency}
                          onChange={handleCurrencyChange}
                          selectOptions={{ options: SUPPORT_CURRENCY_OPTIONS }}
                        />
                        <div className="support-default-amount__custom-text">
                          <span className="support-default-amount__custom-title">{t('default_amounts.custom_amount')}</span>
                          <span className="support-default-amount__custom-description">
                            {t('default_amounts.custom_description', { minimumAmount: defaultMinimumAmount })}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <PublicFormInput
                        placeholder={t('default_amounts.option_placeholder', { index: index + 1, currencySymbol })}
                        type="number"
                        min={3}
                        step={1}
                        inputMode="numeric"
                        value={slot.amount}
                        onChange={event => handleDefaultAmountChange(index, event.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="support-setup__section support-setup__goal">
              <span className="support-setup__section-title">{t('goal.title')}</span>
              <div className="support-setup__goal-fields">
                <PublicFormInput
                  placeholder={t('goal.label')}
                  value={draft.goal.label}
                  onChange={event => setDraft(prev => ({
                    ...prev,
                    goal: {
                      ...prev.goal,
                      label: event.target.value
                    }
                  }))}
                />
                <PublicFormInput
                  placeholder={t('goal.amount', { currencySymbol })}
                  type="number"
                  min={1}
                  value={draft.goal.amount}
                  onChange={event => setDraft(prev => ({
                    ...prev,
                    goal: {
                      ...prev.goal,
                      amount: event.target.value
                    }
                  }))}
                />
              </div>
              {errors.goal && <p className="support-setup__error">{errors.goal}</p>}
            </div>

            {initialConfig?.enabled && (
              <div className="support-setup__danger-zone">
                <button
                  type="button"
                  className="support-setup__disable"
                  onClick={() => setShowDisableConfirmation(true)}
                >
                  {t('actions.disable_support')}
                </button>
              </div>
            )}
          </div>

          <aside className="support-setup__preview">
            <SupportPreview config={formattedPreview} supportedUser={supportedUser} />
          </aside>
        </div>

        <div
          aria-hidden={!isMobilePreviewOpen}
          className={`support-setup__preview-overlay ${isMobilePreviewOpen ? 'support-setup__preview-overlay--visible' : ''}`}
        >
          <div className="support-setup__preview-overlay-header">
            <button
              type="button"
              className="support-setup__preview-back"
              onClick={() => setIsMobilePreviewOpen(false)}
            >
              <BackArrowIcon />
              <span>{t('actions.back')}</span>
            </button>
          </div>
          <SupportPreview
            config={formattedPreview}
            supportedUser={supportedUser}
          />
        </div>

        <footer className="support-setup__footer">
          <ContinueButton onClick={handleValidateAndSave} className="font-size-medium">
            {primaryButtonLabel}
          </ContinueButton>
        </footer>
      </div>
      <SupportBackgroundCropper
        open={showCropper}
        file={imageToCrop}
        onCancel={() => handleCropComplete(null)}
        onComplete={handleCropComplete}
      />
      <ConfirmationDialog
        open={showDisableConfirmation}
        title="Disable support?"
        dialogText="This will hide your support page and prevent new contributions. Existing monthly support subscriptions will remain active and continue billing their supporters."
        closeButtonText="Cancel"
        confirmButtonText={t('actions.disable_support')}
        onClose={() => setShowDisableConfirmation(false)}
        onCloseAction={() => setShowDisableConfirmation(false)}
        onConfirm={handleDisable}
        opacity
      />
    </GenericPopup>
  );
}
